import React, { useState, useEffect } from 'react';
import { Download, Search, Music, Clock, Users, Tag, Filter } from 'lucide-react';

const RoutinesTab = ({ routines, registrations, event, loading, userDetails = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    ageGroup: '',
    level: ''
  });
  
  // Debug logging
  useEffect(() => {
    console.log('RoutinesTab - Event:', event);
    console.log('RoutinesTab - Routines:', routines);
    console.log('RoutinesTab - Loading:', loading);
    console.log('RoutinesTab - User Details:', userDetails);
    
    // Check if routines have the expected structure
    if (routines && routines.length > 0) {
      const firstRoutine = routines[0];
      console.log('First routine structure:', {
        hasId: !!firstRoutine.id,
        hasName: !!firstRoutine.name,
        hasStudioName: !!firstRoutine.studioName,
        hasCategory: !!firstRoutine.category,
        hasAgeGroup: !!firstRoutine.ageGroup,
        hasLevel: !!firstRoutine.level,
        hasDuration: !!firstRoutine.duration,
        hasDancers: Array.isArray(firstRoutine.dancers),
        dancersLength: firstRoutine.dancers?.length || 0,
        hasAudioUrl: !!firstRoutine.audioUrl,
        hasSongFileUrl: !!firstRoutine.songFileUrl,
        songFileName: firstRoutine.songFileName,
        userId: firstRoutine.userId,
        fullObject: firstRoutine // Log the full object for inspection
      });
    }
  }, [routines, event, loading, userDetails]);
  
  // Helper function to get user's full name
  const getUserFullName = (userId) => {
    if (!userId) return 'No User ID';
    
    // First try to find the registration for this userId
    const registration = registrations.find(reg => reg.userId === userId);
    if (registration && (registration.userFirstName || registration.userLastName)) {
      console.log(`Using user details from registration: ${registration.userFirstName} ${registration.userLastName}`);
      return `${registration.userFirstName || ''} ${registration.userLastName || ''}`.trim();
    }
    
    console.log(`Getting full name for userId: ${userId}`);
    console.log(`User details for ${userId}:`, userDetails[userId]);
    
    const user = userDetails[userId];
    if (!user) {
      console.log(`No user details found for userId: ${userId}`);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Check if user is a string (might be an error message)
    if (typeof user === 'string') {
      console.log(`User details for ${userId} is a string:`, user);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Try to extract name from different possible structures
    let firstName = '';
    let lastName = '';
    let email = '';
    
    // Direct properties
    if (user.firstName) firstName = user.firstName;
    if (user.lastName) lastName = user.lastName;
    if (user.email) email = user.email;
    
    // Nested properties
    if (user.user) {
      if (user.user.firstName) firstName = user.user.firstName;
      if (user.user.lastName) lastName = user.user.lastName;
      if (user.user.email) email = user.user.email;
    }
    
    // Attributes property (common in Cognito user objects)
    if (user.attributes) {
      if (user.attributes.given_name) firstName = user.attributes.given_name;
      if (user.attributes.family_name) lastName = user.attributes.family_name;
      if (user.attributes.email) email = user.attributes.email;
    }
    
    // Build the full name
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (email) {
      return email;
    }
    
    return ''; // Return empty string if no name or email found
  };
  
  // Get unique filter options
  const categories = [...new Set(routines.map(r => r.category).filter(Boolean))];
  const ageGroups = [...new Set(routines.map(r => r.ageGroup).filter(Boolean))];
  const levels = [...new Set(routines.map(r => r.level).filter(Boolean))];
  
  // Filter routines based on search term and filters
  const filteredRoutines = routines.filter(routine => {
    const searchLower = searchTerm.toLowerCase();
    const userFullName = getUserFullName(routine.userId).toLowerCase();
    
    const matchesSearch = 
      (routine.name && routine.name.toLowerCase().includes(searchLower)) ||
      (routine.studioName && routine.studioName.toLowerCase().includes(searchLower)) ||
      (routine.category && routine.category.toLowerCase().includes(searchLower)) ||
      (routine.songFileName && routine.songFileName.toLowerCase().includes(searchLower)) ||
      userFullName.includes(searchLower);
    
    const matchesCategory = !filters.category || routine.category === filters.category;
    const matchesAgeGroup = !filters.ageGroup || routine.ageGroup === filters.ageGroup;
    const matchesLevel = !filters.level || routine.level === filters.level;
    
    return matchesSearch && matchesCategory && matchesAgeGroup && matchesLevel;
  });
  
  // Simple fallback component for raw data display
  const RawDataDisplay = ({ title, data }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return <div className="p-4 bg-base-200 rounded-lg">No {title.toLowerCase()} data available</div>;
    }
    
    return (
      <div className="p-4 bg-base-200 rounded-lg">
        <h3 className="font-medium mb-2">{title} (Raw Data)</h3>
        <div className="overflow-x-auto">
          <pre className="text-xs bg-base-300 p-2 rounded max-h-60 overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters({
      category: '',
      ageGroup: '',
      level: ''
    });
    setSearchTerm('');
  };
  
  // Export routines as CSV
  const exportRoutinesCSV = () => {
    if (!routines.length) return;
    
    // Define CSV headers
    const headers = [
      'Routine Name',
      'Studio',
      'Category',
      'Age Group',
      'Level',
      'Duration',
      'Dancers',
      'Has Audio',
      'Song File Name'
    ].join(',');
    
    // Create CSV rows
    const csvRows = routines.map(routine => {
      return [
        `"${routine.name || 'Untitled Routine'}"`,
        `"${routine.studioName || 'Unknown Studio'}"`,
        `"${routine.category || 'Uncategorized'}"`,
        `"${routine.ageGroup || 'N/A'}"`,
        `"${routine.level || 'N/A'}"`,
        `"${routine.duration || 'N/A'}"`,
        routine.dancers?.length || 0,
        routine.audioUrl ? 'Yes' : 'No',
        `"${routine.songFileName || ''}"` 
      ].join(',');
    });
    
    // Combine headers and rows
    const csvContent = [headers, ...csvRows].join('\n');
    
    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${event.city}_routines.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header with search and export */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search routines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        <button
          className="btn btn-primary btn-sm flex items-center gap-2"
          onClick={exportRoutinesCSV}
          disabled={!routines.length}
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>
      
      {/* Raw data display for debugging */}
      <div className="bg-base-200 p-4 rounded-lg mb-4">
        <h3 className="font-medium mb-2">Debug Information</h3>
        <p>Loading: {loading ? 'Yes' : 'No'}</p>
        <p>Routines count: {routines.length}</p>
        <p>Filtered routines count: {filteredRoutines.length}</p>
        <p>Categories: {categories.join(', ') || 'None'}</p>
        <p>Age Groups: {ageGroups.join(', ') || 'None'}</p>
        <p>Levels: {levels.join(', ') || 'None'}</p>
        
        {routines.length > 0 && (
          <div className="mt-2">
            <p className="font-medium">First Routine:</p>
            <pre className="bg-base-300 p-2 rounded text-xs mt-1 overflow-auto max-h-40">
              {JSON.stringify(routines[0], null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Fallback display for when the UI components fail */}
      {routines.length > 0 && (
        <div className="mb-4">
          <RawDataDisplay title="Routines" data={routines} />
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-base-200 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4" />
          <h3 className="font-medium">Filters</h3>
          
          {(filters.category || filters.ageGroup || filters.level) && (
            <button
              className="btn btn-ghost btn-xs ml-auto"
              onClick={resetFilters}
            >
              Reset
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Category filter */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Category</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
            >
              <option value="">All Categories</option>
              {categories.map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          {/* Age Group filter */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Age Group</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={filters.ageGroup}
              onChange={(e) => setFilters({...filters, ageGroup: e.target.value})}
            >
              <option value="">All Age Groups</option>
              {ageGroups.map((ageGroup, index) => (
                <option key={index} value={ageGroup}>{ageGroup}</option>
              ))}
            </select>
          </div>
          
          {/* Level filter */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Level</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={filters.level}
              onChange={(e) => setFilters({...filters, level: e.target.value})}
            >
              <option value="">All Levels</option>
              {levels.map((level, index) => (
                <option key={index} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Routines count */}
      <div className="text-sm text-base-content/70">
        Showing {filteredRoutines.length} of {routines.length} routines
      </div>
      
      {/* Routines list */}
      {filteredRoutines.length === 0 ? (
        <div className="text-center py-12 bg-base-200 rounded-lg">
          <p className="text-base-content/70">
            {routines.length === 0
              ? 'No routines found for this event'
              : 'No routines match your search criteria'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>Routine Name</th>
                <th>Studio</th>
                <th>Category</th>
                <th>Age Group</th>
                <th>Level</th>
                <th>Duration</th>
                <th>Dancers</th>
                <th>Audio</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoutines.map((routine, index) => (
                <tr key={index} className="hover">
                  <td className="font-medium">{routine.name || 'Untitled Routine'}</td>
                  <td>
                    <div>
                      <div>{routine.studioName || 'Unknown Studio'}</div>
                      {getUserFullName(routine.userId) && (
                        <div className="text-xs text-base-content/70">
                          {getUserFullName(routine.userId)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {routine.category ? (
                      <span className="badge badge-outline">{routine.category}</span>
                    ) : (
                      <span className="text-base-content/50">-</span>
                    )}
                  </td>
                  <td>{routine.ageGroup || '-'}</td>
                  <td>{routine.level || '-'}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDuration(routine.duration)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{routine.dancers?.length || 0}</span>
                    </div>
                  </td>
                  <td>
                    {routine.audioUrl ? (
                      <div className="tooltip" data-tip="Audio available">
                        <div className="badge badge-success gap-1">
                          <Music className="h-3 w-3" />
                          Yes
                        </div>
                      </div>
                    ) : (
                      <div className="tooltip" data-tip="No audio uploaded">
                        <div className="badge badge-error gap-1">
                          <Music className="h-3 w-3" />
                          No
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RoutinesTab; 